import { Component, Inject, Injector, OnInit, ViewChild } from '@angular/core';
import { InstanceResponseDto } from '@cloud-twin-v2/generated/simulation.generated.client';
import { WidgetSettingsToken } from '@common-modules/dynamic-layout/dynamic-layout.tokens';
import { AppliedFiltersSelector } from '@common-modules/dynamic-layout/state/filters/filters.selectors';
import { BaseSelectorDynamicWidgetComponent } from '@common-modules/redux/components/base-selector-dynamic-widget.component';
import { StateWidgetSettings } from '@common-modules/redux/models/state-widget-settings';
import { ReduxStateService } from '@common-modules/redux/redux-state.service';
import { DataBindingFilters } from '@common-modules/shared/filters/component-filters/data-binding-filters';
import { ObjectHelperService } from '@common-modules/shared/helpers/object-helper.service';
import { PendingChangesManagerService } from '@common-modules/shared/pending-changes/services/pending-changes-manager.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter } from 'rxjs/operators';
import { ModelsSelectionComponent } from '../models-selection/models-selection.component';
import { ModelStateFields } from '../models/model-state-fields';
import { ModelVersionSharedService } from '../services/model-version-shared.service';

const COMPONENT_SELECTOR = 'tc-model-version-selection-widget';
export const MODEL_VERSION_SELECTOR_COMPONENT_INSTANCE = `${COMPONENT_SELECTOR}#1`;

@UntilDestroy()
@Component({
  selector: COMPONENT_SELECTOR,
  templateUrl: './model-version-selection-widget.component.html',
  styleUrls: ['./model-version-selection-widget.component.scss'],
  providers: [ReduxStateService],
  standalone: false,
})
export class ModelVersionSelectionWidgetComponent
  extends BaseSelectorDynamicWidgetComponent
  implements OnInit
{
  filters: Map<string, any>;

  get selectedFieldName(): string {
    return ModelStateFields.selectedModel;
  }

  get componentName(): string {
    return 'ModelVersionSelectionWidgetComponent';
  }

  @ViewChild('modelsSelection') modelsSelectionComponent: ModelsSelectionComponent;

  constructor(
    injector: Injector,
    @Inject(WidgetSettingsToken) widgetSettings: StateWidgetSettings,
    pendingChangesService: PendingChangesManagerService,
    private readonly _objectHelperService: ObjectHelperService,
    private readonly _modelVersionSharedService: ModelVersionSharedService
  ) {
    super(injector, widgetSettings, pendingChangesService);

    this.pageId = widgetSettings.page;
  }

  onWidgetInit(): void {
    this.emitWidgetInit();

    this._modelVersionSharedService.instanceToDelete$
      .pipe(
        filter((instanceId) => !!instanceId),
        untilDestroyed(this)
      )
      .subscribe((instanceId: string) => {
        this.removeInstance(instanceId);
      });

    this._modelVersionSharedService.instanceToUpdate$
      .pipe(
        filter((instanceId) => !!instanceId),
        untilDestroyed(this)
      )
      .subscribe((update) => {
        const { instanceId, snapshotIds } = update;
        this.updateSnapshotsOfInstance(instanceId, snapshotIds);
      });

    this._modelVersionSharedService.snapshotToUpdate$
      .pipe(
        filter((instanceId) => !!instanceId),
        untilDestroyed(this)
      )
      .subscribe((update) => {
        const { instanceId, snapshotId, scenarioIds } = update;
        this.updateScenariosOfSnapshot(instanceId, snapshotId, scenarioIds);
      });

    this._state
      .select(new AppliedFiltersSelector())
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (dbFilter: DataBindingFilters) => {
          if (!dbFilter) {
            return;
          }
          this.filters = this.setFiltersMap(dbFilter);
        },
      });
  }

  removeInstance(instanceId: string): void {
    this.modelsSelectionComponent?.removeInstance(instanceId);
  }

  updateSnapshotsOfInstance(instanceId: string, snapshotIds: string[]) {
    this.modelsSelectionComponent?.updateSnapshotsOfInstances(instanceId, snapshotIds);
  }

  updateScenariosOfSnapshot(instanceId: string, snapshotId: string, scenarioIds: string[]) {
    this.modelsSelectionComponent?.updateScenariosOfSnapshot(instanceId, snapshotId, scenarioIds);
  }

  onSelectedRow(data: InstanceResponseDto): void {
    this.checkPendingChanges(this.pageId).subscribe((_) => {
      this.setSelected(data);
    });
  }

  private setFiltersMap(dataBindingFilters: DataBindingFilters): Map<string, any> {
    const iFilters = Array.from(dataBindingFilters.filters.values()).map((x) =>
      x.getFiltersValues()
    );

    const filtersToApply = this._objectHelperService.joinMaps(iFilters);

    return this._objectHelperService.clone(filtersToApply);
  }
}
